import { Component, ElementRef, input, output, signal, viewChild } from '@angular/core';
import {
  LEGAL_EFFECTIVE_DATE,
  LEGAL_VERSION,
  PRIVACY_SECTIONS,
  TERMS_SECTIONS,
} from '../../../features/public/legal/legal-content';
import { LegalSectionsView } from '../../../features/public/legal/legal-sections-view';

/**
 * Popup บังคับให้ผู้ใช้ยอมรับ Terms + Privacy Policy ก่อนใช้งาน
 * — trigger เมื่อ user.hasAcceptedTerms === false (login ครั้งแรก / มี bump version)
 * — รวม Terms + Privacy เป็นหน้าเดียวใน popup (scroll ครั้งเดียวจบ)
 * — checkbox enable เสมอ; เมื่อกดติ๊ก ระบบจะ auto-scroll ไปล่างสุดให้
 */
@Component({
  selector: 'app-legal-consent-dialog',
  standalone: true,
  imports: [LegalSectionsView],
  template: `
    @if (visible()) {
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-10002 flex items-center justify-center p-4"
           data-testid="legal-consent-dialog">
        <div class="bg-white rounded-2xl w-full max-w-3xl border border-border-subtle my-8 flex flex-col"
             style="box-shadow: var(--shadow-xl); max-height: calc(100vh - 4rem);">

          <!-- Header -->
          <div class="p-6 border-b border-border-subtle shrink-0">
            <div class="flex items-start gap-3">
              <div class="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                   style="background: linear-gradient(135deg, var(--color-accent-lighter), var(--color-accent-lightest));">
                <span class="material-symbols-outlined text-2xl" style="color: var(--color-accent-dark);">
                  gavel
                </span>
              </div>
              <div class="flex-1">
                <h2 class="text-lg font-bold text-text-primary">ข้อกำหนดการใช้งานและนโยบายความเป็นส่วนตัว</h2>
                <p class="text-xs text-text-secondary mt-0.5">
                  กรุณาเลื่อนอ่านให้ครบก่อนกดยอมรับ · เวอร์ชัน {{ version }} · มีผล {{ effectiveDate }}
                </p>
              </div>
            </div>
          </div>

          <!-- Scrollable body — Terms + Privacy รวมเป็นหน้าเดียว -->
          <div #scrollBody class="flex-1 overflow-y-auto p-6 space-y-10"
               data-testid="legal-consent-scroll-body">
            <!-- ส่วนที่ 1: Terms -->
            <section>
              <div class="flex items-center gap-2 mb-4 pb-2 border-b-2"
                   style="border-color: var(--color-accent-lighter);">
                <span class="material-symbols-outlined text-2xl" style="color: var(--color-accent-dark);">description</span>
                <h3 class="text-lg font-bold" style="color: var(--color-accent-darkest);">ส่วนที่ 1 — ข้อกำหนดและเงื่อนไขการใช้บริการ</h3>
              </div>
              <app-legal-sections-view [sections]="termsSections" />
            </section>

            <!-- ส่วนที่ 2: Privacy -->
            <section>
              <div class="flex items-center gap-2 mb-4 pb-2 border-b-2"
                   style="border-color: var(--color-accent-lighter);">
                <span class="material-symbols-outlined text-2xl" style="color: var(--color-accent-dark);">privacy_tip</span>
                <h3 class="text-lg font-bold" style="color: var(--color-accent-darkest);">ส่วนที่ 2 — นโยบายความเป็นส่วนตัว</h3>
              </div>
              <app-legal-sections-view [sections]="privacySections" />
            </section>
          </div>

          <!-- Footer: checkbox + actions -->
          <div class="border-t border-border-subtle p-6 space-y-3 shrink-0 bg-white">
            <label class="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" [checked]="accepted()"
                     (change)="onCheckboxChange($any($event.target).checked)"
                     data-testid="chk-consent"
                     class="mt-0.5 w-4 h-4 rounded cursor-pointer">
              <span class="text-sm text-text-primary flex-1">
                ข้าพเจ้าได้อ่าน เข้าใจ และยอมรับ<strong>ข้อกำหนดการใช้งาน</strong>
                และ<strong>นโยบายความเป็นส่วนตัว</strong>ข้างต้นแล้ว
              </span>
            </label>

            <div class="flex flex-col sm:flex-row gap-3">
              <button type="button" (click)="onReject()" [disabled]="loading()"
                      data-testid="btn-reject-consent"
                      class="order-2 sm:order-1 flex-1 py-2.5 rounded-full text-sm font-semibold border-2 transition-all cursor-pointer disabled:opacity-50"
                      style="border-color: #dc2626; color: #dc2626; background: transparent;">
                ไม่ยอมรับ (ออกจากระบบ)
              </button>
              <button type="button" (click)="onAccept()" [disabled]="!accepted() || loading()"
                      data-testid="btn-accept-consent"
                      class="order-1 sm:order-2 flex-1 py-2.5 rounded-full text-sm font-semibold text-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      style="background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark)); box-shadow: 0 2px 8px rgba(34,197,94,0.3);">
                @if (loading()) {
                  <span class="material-symbols-outlined animate-spin align-middle text-[18px]">progress_activity</span>
                  กำลังบันทึก...
                } @else {
                  ยอมรับและใช้งานต่อ
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class LegalConsentDialog {
  /** true เมื่อควรแสดง popup */
  readonly visible = input<boolean>(false);
  /** true ระหว่างรอ accept API — disable ปุ่ม + แสดง spinner */
  readonly loading = input<boolean>(false);

  /** event เมื่อผู้ใช้ยอมรับ — parent จะ POST /api/user/legal/accept */
  readonly accepted_event = output<void>({ alias: 'accepted' });
  /** event เมื่อผู้ใช้กดไม่ยอมรับ — parent จะ logout */
  readonly rejected = output<void>();

  protected readonly termsSections = TERMS_SECTIONS;
  protected readonly privacySections = PRIVACY_SECTIONS;
  protected readonly version = LEGAL_VERSION;
  protected readonly effectiveDate = LEGAL_EFFECTIVE_DATE;

  /** ref ไปยัง scrollable body — ใช้ scroll ไปล่างสุดเมื่อกดติ๊ก */
  private readonly scrollBody = viewChild<ElementRef<HTMLDivElement>>('scrollBody');

  /** state ของ checkbox — กด "ยอมรับ" ได้เมื่อ true */
  protected readonly accepted = signal(false);

  /** กดติ๊ก checkbox → set state + ถ้าติ๊กยอมรับ auto-scroll ไปล่างสุดให้เห็นปุ่ม */
  protected onCheckboxChange(checked: boolean): void {
    this.accepted.set(checked);
    if (checked) {
      const el = this.scrollBody()?.nativeElement;
      el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }

  protected onAccept(): void {
    if (!this.accepted()) return;
    this.accepted_event.emit();
  }

  protected onReject(): void {
    this.rejected.emit();
  }
}
